import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, Shield, FileText, Clock } from 'lucide-react';

export const ApplicationMain = () => {
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get('customerid');
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    // Hide confetti after 3 seconds
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 pt-20">
      <Helmet>
        <title>Application Submitted Successfully | Trademark Infinite</title>
        <meta name="description" content="Your trademark application has been submitted successfully" />
      </Helmet>

      {/* Confetti Effect */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-fall"
              style={{
                left: `${Math.random() * 100}%`,
                top: `-${Math.random() * 20}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 2}s`
              }}
            >
              <div
                className="w-3 h-3 rounded-full"
                style={{
                  backgroundColor: ['#f97316', '#fb923c', '#fdba74', '#fcd34d'][Math.floor(Math.random() * 4)]
                }}
              />
            </div>
          ))}
        </div>
      )}

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Success Card */}
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Header with Success Icon */}
            <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-12 text-center relative overflow-hidden">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full blur-2xl animate-pulse"></div>
                <div className="absolute bottom-10 right-10 w-32 h-32 bg-white rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s' }}></div>
              </div>
              <div className="relative z-10">
                <div className="inline-flex items-center justify-center w-24 h-24 bg-white rounded-full mb-6 shadow-2xl">
                  <CheckCircle className="text-green-600" size={56} />
                </div>
                <h1 className="text-4xl md:text-5xl font-black mb-4">Application Submitted!</h1>
                <p className="text-xl text-white/95 max-w-2xl mx-auto">
                  Thank you for choosing Trademark Infinite. Your application has been successfully received.
                </p>
              </div>
            </div>

            {/* Customer ID Section */}
            {customerId && (
              <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-8 border-y border-orange-200">
                <div className="text-center">
                  <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-2">Your Customer ID</p>
                  <div className="inline-block bg-white px-8 py-4 rounded-xl shadow-lg">
                    <p className="text-3xl font-black text-orange-600 font-mono">{customerId}</p>
                  </div>
                  <p className="text-sm text-gray-600 mt-3">Please save this ID for future reference</p>
                </div>
              </div>
            )}

            {/* What Happens Next */}
            <div className="p-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What Happens Next?</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-6 p-6 bg-gray-50 rounded-2xl hover:bg-orange-50 transition-all">
                  <div className="flex-shrink-0 w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center">
                    <FileText className="text-orange-600" size={28} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">1. Application Review</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Our expert team will review your application details within 24 hours to ensure everything is accurate and complete.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-6 p-6 bg-gray-50 rounded-2xl hover:bg-orange-50 transition-all">
                  <div className="flex-shrink-0 w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center">
                    <Shield className="text-orange-600" size={28} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">2. Attorney Assignment</h3>
                    <p className="text-gray-600 leading-relaxed">
                      A licensed USPTO attorney will be assigned to handle your trademark application professionally.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-6 p-6 bg-gray-50 rounded-2xl hover:bg-orange-50 transition-all">
                  <div className="flex-shrink-0 w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center">
                    <Clock className="text-orange-600" size={28} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">3. Confirmation Email</h3>
                    <p className="text-gray-600 leading-relaxed">
                      You will receive a confirmation email with detailed next steps and timeline information.
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Support */}
              <div className="mt-12 p-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl text-white text-center">
                <h3 className="text-2xl font-bold mb-3">Need Immediate Assistance?</h3>
                <p className="text-lg text-white/90 mb-6">Our support team is here to help you</p>
                <div className="flex flex-wrap gap-4 justify-center">
                  <a 
                    href="tel:+18882016668" 
                    className="inline-flex items-center gap-2 bg-white text-orange-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all shadow-lg"
                  >
                    📞 Call: +1 (888) 201-6668
                  </a>
                  <a 
                    href="mailto:info@trademarkinfinite.com" 
                    className="inline-flex items-center gap-2 bg-white text-orange-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all shadow-lg"
                  >
                    ✉️ Email Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fall {
          to {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }
        .animate-fall {
          animation: fall linear forwards;
        }
      `}</style>
    </div>
  );
};
